import { Box, Button, Grid } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { InputField } from "../../../../components/Form/InputField";
import { TextAreaField } from "../../../../components/Form/TextAreaField";
import { SelectField } from "../../../../components/Form/SelectField";
import { useAppSelector } from "../../../../Redux/hooks";
import {
  createCampaign,
  updateCampaign,
} from "../../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";

type PropsCreateCampaignModal = {
  onClose: () => void;
  onCreated: () => void;
  mode?: "create" | "edit";
  selectedCampaign?: any;
};

type CampaignStatus = "draft" | "active" | "paused" | "expired" | "revoked";

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Paused", value: "paused" },
  { label: "Expired", value: "expired" },
  { label: "Revoked", value: "revoked" },
];

const createStatusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Paused", value: "paused" },
];

const createCampaignSchema = Yup.object({
  campaign_key: Yup.string().trim().required("Campaign Key is required"),
  title: Yup.string().trim().required("Title is required"),
  description: Yup.string().trim().required("Description is required"),
  status: Yup.string()
    .oneOf(["draft", "active", "paused", "expired", "revoked"])
    .required("Status is required"),
});

const CreateCampaignModal: React.FC<PropsCreateCampaignModal> = ({
  onClose,
  onCreated,
  mode = "create",
  selectedCampaign,
}) => {
  const createdByEmail = useAppSelector(
    (state) => state.roleAndPermission?.email || ""
  );
  const isEditMode = mode === "edit";
  const statusDropdownOptions = isEditMode ? statusOptions : createStatusOptions;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        campaign_key: selectedCampaign?.campaign_key || "",
        title: selectedCampaign?.title || "",
        description: selectedCampaign?.description || "",
        status: (selectedCampaign?.status || "draft") as CampaignStatus,
      }}
      validationSchema={createCampaignSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          if (isEditMode) {
            if (!selectedCampaign?.id) {
              notifyError("Campaign id is missing");
              return;
            }
            await updateCampaign(selectedCampaign.id, { status: values.status });
            notifySuccess("Campaign updated successfully");
          } else {
            const payload = {
              campaign_key: values.campaign_key.trim(),
              title: values.title.trim(),
              description: values.description.trim(),
              created_by: createdByEmail,
              status: values.status,
            };
            await createCampaign(payload);
            notifySuccess("Campaign created successfully");
          }
          onCreated();
          onClose();
        } catch (error: any) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            (isEditMode ? "Failed to update campaign" : "Failed to create campaign");
          notifyError(Array.isArray(message) ? message[0] : message);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ mt: 1, pr: { md: 1 } }}>
              <InputField
                label="Campaign Key"
                name="campaign_key"
                placeholder="Enter campaign key"
                isRequired
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: 1, pl: { md: 1 } }}>
              <InputField
                label="Title"
                name="title"
                placeholder="Enter title"
                isRequired
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextAreaField
                label="Description"
                name="description"
                placeholder="Enter description"
                rows={4}
                isRequired
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: 2 }}>
              <SelectField
                label="Status"
                name="status"
                value={values.status}
                options={statusDropdownOptions}
                isRequired
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ ml: 2 }}>
              {isEditMode ? "Update Campaign" : "Create Campaign"}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default CreateCampaignModal;

